import React, { useState } from 'react';
import { Button, Form, InputGroup, Spinner, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../../shared/store';
import { SearchCustomers } from '../../services/CustomerAPI';
import Customer from '../../model/Customer';
import PolicyApi from '../../services/PolicyAPI';
import { toast } from 'react-toastify';
import * as constant from '../../../shared/util/constant';
import Logger from '../../../shared/services/Logger';
import { storeCustomerPolicies } from '../../customer-slice/Customer-slice';
import CustomerPolicyListService from '../CustomerPolicyListService';
import './Customer-Policy-Action.scss';

export interface ICustomerPolicyMoveProps {
    policyId: string;
    currentCustId: string;
    onCloseModal: () => void;
    onMoveSuccess?: () => void;
}

const CustomerPolicyMove: React.FunctionComponent<ICustomerPolicyMoveProps> = (props) => {
    const { policyId, currentCustId, onCloseModal, onMoveSuccess } = props;
    const dispatch = useDispatch();

    const [accountNumber, setAccountNumber] = useState<string>('');
    const [customerName, setCustomerName] = useState<string>('');
    const [targetCustomerId, setTargetCustomerId] = useState<string>('');
    const [validationMessage, setValidationMessage] = useState<string>('');
    const [isValidated, setIsValidated] = useState<boolean>(false);
    const [isValidating, setIsValidating] = useState<boolean>(false);
    const [isMoving, setIsMoving] = useState<boolean>(false);

    const handleValidate = async () => {
        const trimmed = accountNumber.trim();
        if (!trimmed) {
            setValidationMessage(constant.MovePolicy.PLEASE_ENTER_CS2_ACCOUNT_Number);
            setIsValidated(false);
            setCustomerName('');
            setTargetCustomerId('');
            return;
        }

        setIsValidating(true);
        setValidationMessage('');
        setCustomerName('');
        setTargetCustomerId('');
        setIsValidated(false);

        try {
            const response = await SearchCustomers({ custNumber: Number(trimmed) });
            const customers: Customer[] = response.data;

            if (!customers || customers.length === 0) {
                setValidationMessage(constant.MovePolicy.ACCOUNT_NOT_FOUND);
                setIsValidated(false);
                return;
            }

            const customer = customers[0];

            if (customer.id === currentCustId) {
                setValidationMessage(constant.MovePolicy.POLICY_CANNOT_MOVED);
                setIsValidated(false);
                return;
            }

            const name = [customer.firstName, customer.lastName].filter(Boolean).join(' ');
            setCustomerName(name);
            setTargetCustomerId(customer.id || '');
            setIsValidated(true);
            setValidationMessage('');
        } catch (error) {
            setValidationMessage('Account not found.');
            setIsValidated(false);
            Logger.logError(error?.toString() || 'Error validating account');
        } finally {
            setIsValidating(false);
        }
    };

    const customerPolicyListService = new CustomerPolicyListService();

    const handleMove = async () => {
        if (!targetCustomerId || !policyId || !currentCustId) return;

        setIsMoving(true);
        try {
            const policyApi = new PolicyApi();
            await policyApi.movePolicy(policyId, currentCustId, targetCustomerId);
            const updatedPolicies = await customerPolicyListService.fetchCustomerPolicyDetails(currentCustId);
            dispatch(storeCustomerPolicies(updatedPolicies || []));
            toast.success('Policy moved successfully.', {
                position: toast.POSITION.TOP_RIGHT,
                autoClose: constant.AUTO_CLOSE
            });
            onMoveSuccess?.();
            onCloseModal();
        } catch (error) {
            toast.error('Failed to move the policy. Please try again.', {
                position: toast.POSITION.TOP_RIGHT,
                autoClose: constant.AUTO_CLOSE
            });
            Logger.logError(error?.toString() || 'Error moving policy');
        } finally {
            setIsMoving(false);
        }
    };

    const handleAccountNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.replace(/[^0-9]/g, '');
        setAccountNumber(value);
        setIsValidated(false);
        setCustomerName('');
        setTargetCustomerId('');
        setValidationMessage('');
    };

    return (
        <div className="p-3">
            <Form.Group className="mb-3">
                <Form.Label>CS2 Account Number</Form.Label>
                <InputGroup>
                    <Form.Control
                        type="text"
                        placeholder={constant.MovePolicy.ENTER_CS2_ACCOUNT_Number}
                        value={accountNumber}
                        onChange={handleAccountNumberChange}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleValidate(); }}
                    />
                    <Button
                        variant="primary"
                        onClick={handleValidate}
                        disabled={isValidating || !accountNumber.trim()}
                    >
                        {isValidating ? (
                            <Spinner animation="border" size="sm" />
                        ) : (
                            <FontAwesomeIcon icon="search" />
                        )}
                        {' '}Validate
                    </Button>
                </InputGroup>
            </Form.Group>
            
            {isValidated && customerName && (
                <Form.Group className="mb-3">
                    <Form.Label>Customer Name</Form.Label>
                    <Form.Control type="text" value={customerName} readOnly disabled />
                </Form.Group>
            )}

            {validationMessage && (
                <Alert variant="danger" className="mb-3" style={{ backgroundColor: '#ff00008c', borderColor: '#ff00008c' }}>
                    <FontAwesomeIcon icon="exclamation-triangle" className="me-2" />
                    {validationMessage}
                </Alert>
            )}

            <div className="d-flex justify-content-end gap-2 mt-3">
                <Button variant="primary" onClick={onCloseModal} disabled={isMoving}>
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    onClick={handleMove}
                    disabled={!isValidated || isMoving}
                >
                    {isMoving ? (
                        <>
                            <Spinner animation="border" size="sm" className="me-1" />
                            Moving...
                        </>
                    ) : (
                        'Move'
                    )}
                </Button>
            </div>
        </div>
    );
};

export default CustomerPolicyMove;
